/**
 * Team hooks (task 636).
 *
 * React Query wrappers over `teamApi` (account membership, the role catalog,
 * and task-634 invitations). Members and invitations are the two halves of one
 * "who has access" list on the Team page, so a mutation to either invalidates
 * both keys — a fresh invite must appear in the same table as the members, and
 * an accepted/revoked invite must drop out of it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  teamApi,
  type CreateInviteInput,
  type MemberRoleUpdate,
  type RoleDefinition,
  type TeamInvite,
  type TeamMember,
} from '../services/team';

export const TEAM_MEMBERS_KEY = ['account', 'members'] as const;
export const TEAM_INVITES_KEY = ['account', 'invitations'] as const;
export const ROLE_CATALOG_KEY = ['account', 'roles'] as const;

/** Active members of the caller's account. */
export const useTeamMembers = () =>
  useQuery<TeamMember[]>({
    queryKey: TEAM_MEMBERS_KEY,
    queryFn: () => teamApi.listMembers().then((res) => res.data.data),
    staleTime: 60_000,
    retry: 1,
  });

/** Pending invitations for the account (task 634). */
export const useTeamInvitations = () =>
  useQuery<TeamInvite[]>({
    queryKey: TEAM_INVITES_KEY,
    queryFn: () => teamApi.listInvitations().then((res) => res.data.data),
    staleTime: 60_000,
    retry: 1,
  });

/**
 * The AMS role-definition catalog. Long-lived — role summaries change rarely —
 * and the single source both the Roles card and the invite modal's role list
 * read from, so neither hardcodes role capabilities.
 */
export const useRoleCatalog = () =>
  useQuery<RoleDefinition[]>({
    queryKey: ROLE_CATALOG_KEY,
    queryFn: () => teamApi.listRoles().then((res) => res.data.data),
    staleTime: 10 * 60_000,
    retry: 1,
  });

/** Change a member's role (owner/admin). Refreshes the members list. */
export const useUpdateMemberRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, update }: { userId: string; update: MemberRoleUpdate }) =>
      teamApi.updateMemberRole(userId, update).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY });
    },
  });
};

/** Create an invitation. Refreshes the invites list (and members, for safety). */
export const useCreateInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInviteInput) =>
      teamApi.createInvitation(input).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_INVITES_KEY });
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY });
    },
  });
};

/** Resend an invitation (re-email + refresh expiry). */
export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamApi.resendInvitation(id).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_INVITES_KEY });
    },
  });
};

/** Revoke a pending invitation. */
export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamApi.revokeInvitation(id).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_INVITES_KEY });
    },
  });
};

/**
 * Pulls a stable error code off a failed mutation so the caller can distinguish
 * "already a member" / "already invited" from a generic failure and render the
 * reason inline.
 *
 * The axios response interceptor (`services/api`) reshapes every rejection into
 * an `APIError` whose `.response` field holds the parsed error BODY (not the
 * axios response object). innovate-service error envelopes carry `{ code }`;
 * AMS-relayed ones may carry `{ error }`. Anything else is `undefined`.
 */
export const teamErrorCode = (error: unknown): string | undefined => {
  const body = (error as { response?: { code?: string; error?: string } })?.response;
  return body?.code ?? body?.error;
};
